import React, { useState, useRef, useCallback, useEffect } from 'react';
import './DraggableFloat.scss';

/**
 * DraggableFloat - A floating draggable element that snaps to screen edges (magnet).
 * Shows an image that navigates to a URL on click.
 * Has a close button at top-right corner.
 *
 * @param {Object} props
 * @param {string} props.imageUrl - Image URL to display
 * @param {string} props.redirectUrl - URL to navigate when image is clicked
 */
const DraggableFloat = ({ imageUrl, redirectUrl }) => {
  const [visible, setVisible] = useState(true);
  const [position, setPosition] = useState({ x: window.innerWidth - 102, y: window.innerHeight - 220 });
  const dragRef = useRef(null);
  const dragState = useRef({ isDragging: false, startX: 0, startY: 0, startPosX: 0, startPosY: 0, hasMoved: false });

  // Re-snap position on window resize
  useEffect(() => {
    const handleResize = () => {
      const elementWidth = 90;
      const edgePadding = 12;
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;

      setPosition((prev) => {
        const centerX = prev.x + elementWidth / 2;
        const newX = centerX < screenWidth / 2 ? edgePadding : screenWidth - elementWidth - edgePadding;
        const newY = Math.min(prev.y, screenHeight - 120);
        return { x: newX, y: Math.max(0, newY) };
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleStart = useCallback((clientX, clientY) => {
    dragState.current = {
      isDragging: true,
      startX: clientX,
      startY: clientY,
      startPosX: position.x,
      startPosY: position.y,
      hasMoved: false,
    };
  }, [position]);

  const handleMove = useCallback((clientX, clientY) => {
    if (!dragState.current.isDragging) return;

    const dx = clientX - dragState.current.startX;
    const dy = clientY - dragState.current.startY;

    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
      dragState.current.hasMoved = true;
    }

    const newX = dragState.current.startPosX + dx;
    const newY = dragState.current.startPosY + dy;

    const maxX = window.innerWidth - 90;
    const maxY = window.innerHeight - 120;

    setPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY)),
    });
  }, []);

  const handleEnd = useCallback(() => {
    if (!dragState.current.isDragging) return;
    dragState.current.isDragging = false;

    // Magnet: snap to nearest horizontal edge with padding
    const elementWidth = 90;
    const edgePadding = 12;
    const screenWidth = window.innerWidth;

    setPosition((prev) => {
      const centerX = prev.x + elementWidth / 2;
      return {
        x: centerX < screenWidth / 2 ? edgePadding : screenWidth - elementWidth - edgePadding,
        y: prev.y,
      };
    });
  }, []);

  // Touch events
  const onTouchStart = (e) => {
    const touch = e.touches[0];
    handleStart(touch.clientX, touch.clientY);
  };

  const onTouchMove = (e) => {
    const touch = e.touches[0];
    handleMove(touch.clientX, touch.clientY);
  };

  const onTouchEnd = () => {
    handleEnd();
  };

  // Mouse events
  const onMouseDown = (e) => {
    handleStart(e.clientX, e.clientY);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const onMouseMove = (e) => {
    handleMove(e.clientX, e.clientY);
  };

  const onMouseUp = () => {
    handleEnd();
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
  };

  const handleImageClick = () => {
    if (!dragState.current.hasMoved && redirectUrl) {
      window.open(redirectUrl, '_blank');
    }
  };

  const handleClose = (e) => {
    e.stopPropagation();
    setVisible(false);
  };

  if (!visible || !imageUrl) return null;

  return (
    <div
      className="draggable-float"
      ref={dragRef}
      style={{ left: position.x, top: position.y }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onMouseDown={onMouseDown}
      data-testid="draggable-float"
    >
      <button
        className="draggable-float__close"
        onClick={handleClose}
        data-testid="draggable-float-close"
      >
        &times;
      </button>
      <img
        className="draggable-float__image"
        src={imageUrl}
        alt="redirect"
        onClick={handleImageClick}
        draggable={false}
        data-testid="draggable-float-image"
      />
    </div>
  );
};

export default DraggableFloat;
